import { RecaptchaError } from '../errors/recaptcha';

/**
 * This module provides utility functions to load the Google reCAPTCHA script and retrieve reCAPTCHA tokens.
 * It ensures that the reCAPTCHA script is loaded only once and provides a way to get tokens for specific actions.
 */
export const loadRecaptchaScript = (siteKey: string | undefined): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!siteKey) {
      reject(new RecaptchaError('the reCAPTCHA site key is not configured'));
      return;
    }
    if (window.grecaptcha) return resolve();

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new RecaptchaError('Failed to load reCAPTCHA'));

    if (!document.body) {
      window.addEventListener(
        'DOMContentLoaded',
        () => {
          document.body.appendChild(script);
        },
        { once: true },
      );
    } else {
      document.body.appendChild(script);
    }
  });
};

export const getRecaptchaToken = async (siteKey: string, action = 'submit'): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!window.grecaptcha) {
      reject(new RecaptchaError('reCAPTCHA not loaded'));
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(siteKey, { action })
        .then((token) => {
          resolve(token);
        })
        .catch((err) => {
          reject(new RecaptchaError(err instanceof Error ? err.message : 'Unknown error'));
        });
    });
  });
};
